import { useEffect, useRef, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { A4DpiToDetailsMap } from 'src/consumer/constants'
import { toast } from 'react-toastify'

/**
 * PrintRxDrawerComponent
 * 
 * Shows the same content as the quick print button on PrescriptionMakingPage.
 * Follows the exact same flow: scroll to render, wait for multipass algorithm,
 * wait for layout settlement, then clone pages and display in drawer.
 * 
 * @param {Object} props
 * @param {Object} props.prescriptionRef - Ref to PrescriptionMakingPage component
 * @param {Function} props.onClose - Callback when drawer should close
 */
export default function PrintRxDrawerComponent({ prescriptionRef, onClose }) {
  const [clonedPagesHTML, setClonedPagesHTML] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const containerRef = useRef(null)
  const printTriggeredRef = useRef(false)
  const totalPagesRef = useRef(0)

  // Override DrawerContent padding and overflow for print_rx
  useEffect(() => {
    // Find the DrawerContent parent and override its styles
    const findAndOverrideDrawerContent = () => {
      const container = document.querySelector('[data-print-rx-drawer-container="true"]')
      if (!container) return

      // Force container width to 100%
      container.style.width = '100%'
      container.style.minWidth = '100%'
      container.style.maxWidth = '100%'
      container.style.margin = '0'
      container.style.padding = '0'
      container.style.boxSizing = 'border-box'

      // Find ALL parent elements and override any with padding, overflow-x: hidden, or width constraints
      let parent = container.parentElement
      const overriddenParents = []
      while (parent && parent !== document.body) {
        const computed = window.getComputedStyle(parent)
        const paddingLeft = parseInt(computed.paddingLeft) || 0
        const paddingRight = parseInt(computed.paddingRight) || 0
        const hasPadding = paddingLeft > 0 || paddingRight > 0
        const hasOverflowHidden = computed.overflowX === 'hidden' || computed.overflow === 'hidden'
        const hasWidthConstraint = computed.width && computed.width !== 'auto' && computed.width !== '100%'
        const parentRect = parent.getBoundingClientRect()
        
        if (hasPadding || hasOverflowHidden || hasWidthConstraint) {
          if (hasPadding) {
            // Remove all padding, not just left/right
            parent.style.padding = '0'
            parent.style.paddingLeft = '0'
            parent.style.paddingRight = '0'
            parent.style.paddingTop = '0'
            parent.style.paddingBottom = '0'
          }
          if (hasOverflowHidden) {
            parent.style.overflowX = 'visible'
            parent.style.overflow = 'visible'
          }
          if (hasWidthConstraint) {
            parent.style.width = '100%'
            parent.style.minWidth = '100%'
            parent.style.maxWidth = '100%'
          }
          overriddenParents.push({
            tagName: parent.tagName,
            className: parent.className,
            originalPadding: computed.padding,
            originalOverflowX: computed.overflowX,
            originalOverflow: computed.overflow,
            originalWidth: computed.width,
            parentRect: { left: parentRect.left, right: parentRect.right, width: parentRect.width },
            hadPadding: hasPadding,
            hadOverflowHidden: hasOverflowHidden,
            hadWidthConstraint: hasWidthConstraint,
          })
        }
        parent = parent.parentElement
      }
      
      // Check container and page visibility
      const containerRect = container.getBoundingClientRect()
      const firstPage = container.querySelector('.prescription-page')
      const firstPageRect = firstPage?.getBoundingClientRect()
      const drawerPaper = container.closest('.MuiDrawer-paper')
      const drawerPaperRect = drawerPaper?.getBoundingClientRect()
      const drawerPaperComputed = drawerPaper ? window.getComputedStyle(drawerPaper) : null
      
      // Calculate visible widths
      const viewportWidth = window.innerWidth
      const containerVisibleLeft = Math.max(containerRect.left, 0)
      const containerVisibleRight = Math.min(containerRect.right, viewportWidth)
      const containerVisibleWidth = containerVisibleRight - containerVisibleLeft
      const pageVisibleLeft = firstPageRect ? Math.max(firstPageRect.left, 0) : null
      const pageVisibleRight = firstPageRect ? Math.min(firstPageRect.right, viewportWidth) : null
      const pageVisibleWidth = (pageVisibleLeft !== null && pageVisibleRight !== null) ? pageVisibleRight - pageVisibleLeft : null
      const pageCutoffLeft = firstPageRect ? Math.max(0, 0 - firstPageRect.left) : null
      const pageCutoffRight = firstPageRect ? Math.max(0, firstPageRect.right - viewportWidth) : null
      
      
    }

    // Wait for component to mount, then override
    const timer = setTimeout(() => {
      findAndOverrideDrawerContent()
      // Also force width after a short delay to ensure it takes effect
      // For bottom drawer, we have full width, so just ensure it's 100% and remove padding
      setTimeout(() => {
        const container = document.querySelector('[data-print-rx-drawer-container="true"]')
        if (container) {
          const drawerPaper = container.closest('.MuiDrawer-paper')
          if (drawerPaper) {
            // Use getBoundingClientRect for accurate width (accounts for borders, padding, etc.)
            const paperRect = drawerPaper.getBoundingClientRect()
            const paperComputed = window.getComputedStyle(drawerPaper)
            const paperBorderLeft = parseFloat(paperComputed.borderLeftWidth) || 0
            const paperBorderRight = parseFloat(paperComputed.borderRightWidth) || 0
            const paperPaddingLeft = parseFloat(paperComputed.paddingLeft) || 0
            const paperPaddingRight = parseFloat(paperComputed.paddingRight) || 0
            // Available width inside drawer paper (excluding borders and padding)
            const availableWidth = paperRect.width - paperBorderLeft - paperBorderRight - paperPaddingLeft - paperPaddingRight
            
            // Force container to match available width (should be close to viewport width for bottom drawer)
            container.style.setProperty('width', `${availableWidth}px`, 'important')
            container.style.setProperty('min-width', `${availableWidth}px`, 'important')
            container.style.setProperty('max-width', `${availableWidth}px`, 'important')
            container.style.setProperty('margin', '0', 'important')
            container.style.setProperty('padding', '0', 'important')
            container.style.setProperty('box-sizing', 'border-box', 'important')
            
            const printRxContainer = container.querySelector('.print-rx-container')
            if (printRxContainer) {
              printRxContainer.style.setProperty('width', `${availableWidth}px`, 'important')
              printRxContainer.style.setProperty('min-width', `${availableWidth}px`, 'important')
              printRxContainer.style.setProperty('max-width', `${availableWidth}px`, 'important')
            }
            
            // Force a reflow
            void container.offsetWidth
          }
        }
      }, 200)
    }, 100)

    // Also add a CSS override as backup
    const styleOverride = document.createElement('style')
    styleOverride.id = 'print-rx-drawer-content-override'
    styleOverride.textContent = `
      /* Override DrawerContent padding and overflow for print_rx */
      [data-print-rx-drawer-container="true"] {
        margin: 0 !important;
        padding: 0 !important;
        width: 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      [data-print-rx-drawer-container="true"] > div {
        width: 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        box-sizing: border-box !important;
      }
      .print-rx-container {
        width: 100% !important;
        min-width: 100% !important;
        max-width: 100% !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin-left: 0 !important;
        margin-right: 0 !important;
        box-sizing: border-box !important;
        overflow-x: visible !important;
        overflow-y: auto !important;
      }
      /* Ensure DrawerContent doesn't cut off content - target by containing our container */
      .MuiDrawer-paper:has([data-print-rx-drawer-container="true"]) > div {
        padding: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        overflow-x: visible !important;
        overflow-y: auto !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
        margin: 0 !important;
      }
      /* Fallback for browsers that don't support :has() - target DrawerContent */
      .MuiDrawer-paper [class*="DrawerContent"] {
        overflow-x: visible !important;
        padding: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        padding-top: 0 !important;
        padding-bottom: 0 !important;
        margin: 0 !important;
        width: 100% !important;
        max-width: 100% !important;
        box-sizing: border-box !important;
      }
      /* Target the styled DrawerContent component */
      .MuiDrawer-paper > div[class*="sc-"] {
        padding: 0 !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
        overflow-x: visible !important;
      }
      /* Force all children to respect full width */
      [data-print-rx-drawer-container="true"] * {
        box-sizing: border-box !important;
      }
    `
    document.head.appendChild(styleOverride)

    return () => {
      clearTimeout(timer)
      const style = document.getElementById('print-rx-drawer-content-override')
      if (style) {
        style.remove()
      }
    }
  }, [])

  // Replicate quick print flow exactly
  useEffect(() => {
    const executeQuickPrintFlow = async () => {
      setIsLoading(true)
      printTriggeredRef.current = false

      // Show loading toast
      const loadingToastId = toast.info('Preparing pages for printing...', {
        autoClose: false,
        closeOnClick: false,
        closeButton: false
      })

      try {
        // Get A4 dimensions
        const currentDpiData = A4DpiToDetailsMap?.[96] || A4DpiToDetailsMap[96]
        const widthOfA4Page = currentDpiData?.width || 794
        const heightOfA4Page = currentDpiData?.height || 1123

        // STEP 1: Scroll through document to force all content to render
        toast.update(loadingToastId, {
          render: 'Scrolling to measure all content...',
          type: 'info',
          autoClose: false
        })

        const scrollToRenderAllPages = () => {
          return new Promise((resolve) => {
            const maxScroll = Math.max(
              document.body.scrollHeight,
              document.documentElement.scrollHeight,
              document.body.offsetHeight,
              document.documentElement.offsetHeight,
              document.body.clientHeight,
              document.documentElement.clientHeight
            )

            let currentScroll = 0
            const scrollStep = window.innerHeight * 0.8
            const scrollDelay = 150

            const scrollStepByStep = () => {
              if (currentScroll < maxScroll) {
                currentScroll = Math.min(currentScroll + scrollStep, maxScroll)
                window.scrollTo({ top: currentScroll, behavior: 'auto' })

                window.setTimeout(() => {
                  scrollStepByStep()
                }, scrollDelay)
              } else {
                window.setTimeout(() => {
                  window.scrollTo({ top: 0, behavior: 'auto' })
                  window.requestAnimationFrame(() => {
                    window.requestAnimationFrame(() => {
                      window.setTimeout(() => {
                        resolve()
                      }, 200)
                    })
                  })
                }, 500)
              }
            }

            scrollStepByStep()
          })
        }
        await scrollToRenderAllPages()

        // STEP 2: Wait for dimensions to update after scrolling
        await new Promise((resolve) => {
          window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
              window.setTimeout(() => {
                resolve()
              }, 500)
            })
          })
        })

        // STEP 3: Wait for multipass algorithm to complete
        // Get initial totalPages by counting pages in DOM
        const getCurrentPageCount = () => {
          // Use EXACT same logic as quick print button - exclude pages in PrintRx drawer but include pages in Rx drawer
          let pages = Array.from(document.querySelectorAll('.prescription-page'))
          
          // Filter out pages from hidden containers (same logic as quick print button)
          pages = pages.filter(page => {
            // Check if page itself is visible
            const pageStyle = window.getComputedStyle(page)
            if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
              return false
            }
            
            // Exclude pages in PrintRx drawer (our own drawer) - these are clones we created
            if (page.closest('[data-print-rx-drawer-container="true"]')) {
              return false
            }
            
            // Check if page is in a visible container (not hidden drawer/modal) - same as quick print
            // BUT: Don't exclude pages in Rx drawer even if it's a drawer - we want those pages
            let parent = page.parentElement
            const isInRxDrawer = page.closest('.PMPPrintWrapper') !== null
            while (parent && parent !== document.body) {
              const style = window.getComputedStyle(parent)
              // Skip if parent is hidden (but allow Rx drawer even if it's a drawer)
              if (style.display === 'none' || style.visibility === 'hidden') {
                // If page is in Rx drawer, check if the drawer itself is visible (not just display: none on parent)
                if (isInRxDrawer) {
                  const rxDrawer = page.closest('.PMPPrintWrapper')
                  if (rxDrawer) {
                    const rxDrawerStyle = window.getComputedStyle(rxDrawer)
                    // If Rx drawer is visible, allow the page even if a parent is hidden
                    if (rxDrawerStyle.display !== 'none' && rxDrawerStyle.visibility !== 'hidden') {
                      // Continue checking other parents, but don't exclude based on this parent
                      parent = parent.parentElement
                      continue
                    }
                  }
                }
                return false
              }
              // Skip if parent is a closed drawer (MUI Drawer with display: none)
              // BUT: Don't exclude if it's the Rx drawer (PMPPrintWrapper is inside a drawer)
              if (parent.classList.contains('MuiDrawer-root') && style.display === 'none' && !isInRxDrawer) {
                return false
              }
              parent = parent.parentElement
            }
            return true
          })
          
          
          return pages.length
        }

        const waitForMultipassAlgorithm = () => {
          return new Promise((resolve) => {
            const initialTotalPages = getCurrentPageCount()
            totalPagesRef.current = initialTotalPages
            const maxWait = 15000 // Increased to 15 seconds to allow algorithm more time
            const startTime = Date.now()
            let lastTotalPages = initialTotalPages
            let stableCount = 0
            const requiredStableChecks = 5 // Increased from 3 to 5 to ensure algorithm completes


            const checkTotalPages = () => {
              const currentTotalPages = getCurrentPageCount()
              totalPagesRef.current = currentTotalPages
              const elapsed = Date.now() - startTime


              if (currentTotalPages > initialTotalPages) {
                toast.update(loadingToastId, {
                  render: `Found ${currentTotalPages} page(s). Rendering pages...`,
                  type: 'info',
                  autoClose: false
                })
                resolve()
                return
              }

              if (currentTotalPages > 0) {
                // If we only have 1 page, wait longer - don't resolve too early
                // The multipass algorithm might still be creating page 2
                if (currentTotalPages === 1 && elapsed < 10000) {
                  // If we only have 1 page and haven't waited 10 seconds, keep waiting
                  // Reset stable count to keep checking
                  stableCount = 0
                  lastTotalPages = currentTotalPages
                  window.requestAnimationFrame(() => {
                    window.setTimeout(checkTotalPages, 200)
                  })
                  return
                }
                
                if (currentTotalPages === lastTotalPages) {
                  stableCount++
                  // Require more stable checks if we only have 1 page
                  const requiredChecks = currentTotalPages === 1 ? 10 : requiredStableChecks
                  if (stableCount >= requiredChecks) {
                    // If we only have 1 page after waiting, log a warning but proceed
                    if (currentTotalPages === 1) {
                    } else {
                    }
                    toast.update(loadingToastId, {
                      render: `Found ${currentTotalPages} page(s). Rendering pages...`,
                      type: 'info',
                      autoClose: false
                    })
                    resolve()
                    return
                  }
                } else {
                  stableCount = 0
                }
              }

              lastTotalPages = currentTotalPages

              if (elapsed > maxWait) {
                resolve()
                return
              }

              window.requestAnimationFrame(() => {
                window.setTimeout(checkTotalPages, 200)
              })
            }

            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                window.setTimeout(checkTotalPages, 500) // Increased initial delay from 300 to 500
              })
            })
          })
        }

        // Wait for Rx drawer to be open and pages to be visible
        // The quick print button doesn't wait for multipass - it queries pages directly
        // But we need to wait for the Rx drawer to be open so pages are visible
        const waitForRxDrawerOpen = async () => {
          const maxWait = 5000 // 5 seconds max
          const startTime = Date.now()
          
          while (Date.now() - startTime < maxWait) {
            // Check if Rx drawer is open by looking for pages in it
            const rxDrawer = document.querySelector('.PMPPrintWrapper, [class*="PMPPrintWrapper"]')
            const pagesInRxDrawer = rxDrawer ? Array.from(rxDrawer.querySelectorAll('.prescription-page')) : []
            const allVisiblePages = getCurrentPageCount()
            
            
            // If we have at least 1 page visible anywhere (main page or Rx drawer), proceed
            // The quick print button doesn't require Rx drawer to be open - pages can be on main page
            if (allVisiblePages >= 1 || pagesInRxDrawer.length >= 1) {
              return
            }
            
            await new Promise(resolve => setTimeout(resolve, 200))
          }
          
        }
        
        await waitForRxDrawerOpen()

        // STEP 3: Get initial page count and set it in ref
        const initialPageCount = getCurrentPageCount()
        totalPagesRef.current = initialPageCount
        

        // STEP 4: Wait for all pages to be rendered
        const waitForAllPages = () => {
          return new Promise((resolve) => {
            const expectedPageCount = totalPagesRef.current || 0

            if (expectedPageCount === 0) {
              resolve()
              return
            }

            let lastPageCount = 0
            let stableCount = 0
            const requiredStableChecks = 3
            const maxWait = 5000
            const startTime = Date.now()

            const checkPages = () => {
              const currentPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
                const pageStyle = window.getComputedStyle(page)
                if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
                  return false
                }
                // Don't exclude pages in drawers - only exclude pages in our own PrintRx drawer
                if (page.closest('[data-print-rx-drawer-container="true"]')) {
                  return false
                }
                let parent = page.parentElement
                while (parent && parent !== document.body) {
                  const style = window.getComputedStyle(parent)
                  if (style.display === 'none' || style.visibility === 'hidden') {
                    return false
                  }
                  parent = parent.parentElement
                }
                return true
              })

              const currentCount = currentPages.length

              if (currentCount >= expectedPageCount && currentCount === lastPageCount) {
                stableCount++
                if (stableCount >= requiredStableChecks) {
                  resolve()
                  return
                }
              } else if (currentCount < expectedPageCount) {
                stableCount = 0
              } else if (currentCount === lastPageCount && currentCount > 0) {
                stableCount++
                if (stableCount >= requiredStableChecks * 2) {
                  resolve()
                  return
                }
              } else {
                stableCount = 0
              }

              lastPageCount = currentCount

              if (Date.now() - startTime > maxWait) {
                resolve()
                return
              }

              window.requestAnimationFrame(() => {
                window.setTimeout(checkPages, 200)
              })
            }

            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                window.setTimeout(checkPages, 300)
              })
            })
          })
        }

        // Update toast with current page count
        const currentPageCount = getCurrentPageCount()
        totalPagesRef.current = currentPageCount || totalPagesRef.current || 0
        toast.update(loadingToastId, {
          render: `Waiting for all ${totalPagesRef.current || currentPageCount || '?'} page(s) to render...`,
          type: 'info',
          autoClose: false
        })
        await waitForAllPages()

        // STEP 5: Wait for layout settlement
        // Don't filter out pages in drawers - we need all pages including those in Rx drawer
        let allPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
          const pageStyle = window.getComputedStyle(page)
          if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
            return false
          }
          // Don't exclude pages in drawers - prescription pages might be in Rx drawer
          // Only exclude pages in the PrintRx drawer itself (our own drawer)
          if (page.closest('[data-print-rx-drawer-container="true"]')) {
            return false
          }
          let parent = page.parentElement
          while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent)
            if (style.display === 'none' || style.visibility === 'hidden') {
              return false
            }
            parent = parent.parentElement
          }
          return true
        })
        

        const waitForLayoutSettlement = () => {
          return new Promise((resolve) => {
            let previousHeights = []
            let stableCount = 0
            const requiredStableChecks = 3

            const checkStability = () => {
              const currentAllPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
                const pageStyle = window.getComputedStyle(page)
                if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
                  return false
                }
                // Don't exclude pages in drawers - only exclude pages in our own PrintRx drawer
                if (page.closest('[data-print-rx-drawer-container="true"]')) {
                  return false
                }
                let parent = page.parentElement
                while (parent && parent !== document.body) {
                  const style = window.getComputedStyle(parent)
                  if (style.display === 'none' || style.visibility === 'hidden') {
                    return false
                  }
                  parent = parent.parentElement
                }
                return true
              })

              if (currentAllPages.length > allPages.length) {
                allPages = currentAllPages
              }

              const currentHeights = allPages.map((page) => {
                const rect = page.getBoundingClientRect()
                const scrollHeight = page.scrollHeight
                const effectiveHeight = rect.height > 10 ? rect.height : scrollHeight
                return {
                  height: effectiveHeight,
                  scrollHeight: scrollHeight
                }
              })

              const isStable = previousHeights.length === currentHeights.length &&
                previousHeights.every((prev, idx) =>
                  prev && currentHeights[idx] &&
                  Math.abs(prev.height - currentHeights[idx].height) < 1 &&
                  Math.abs(prev.scrollHeight - currentHeights[idx].scrollHeight) < 1
                )

              if (isStable) {
                stableCount++
                if (stableCount >= requiredStableChecks) {
                  resolve()
                  return
                }
              } else {
                stableCount = 0
              }

              previousHeights = currentHeights

              window.requestAnimationFrame(() => {
                window.setTimeout(checkStability, 100)
              })
            }

            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => {
                window.setTimeout(checkStability, 200)
              })
            })

            window.setTimeout(() => {
              if (stableCount < requiredStableChecks) {
                resolve()
              }
            }, 2000)
          })
        }

        await waitForLayoutSettlement()

        // Wait for pages to have content (not just exist)
        const waitForPageContent = async (pages) => {
          const maxWait = 5000 // 5 seconds max
          const startTime = Date.now()
          
          while (Date.now() - startTime < maxWait) {
            let allPagesHaveContent = true
            for (const page of pages) {
              // Check if page has substantial content (more than just empty structure)
              const hasText = page.textContent && page.textContent.trim().length > 100
              const hasChildren = page.children.length > 0
              const hasImages = page.querySelectorAll('img').length > 0
              const htmlLength = page.outerHTML.length
              
              // Page should have either text content, children, images, or substantial HTML
              if (!hasText && !hasChildren && !hasImages && htmlLength < 1000) {
                allPagesHaveContent = false
                break
              }
            }
            
            if (allPagesHaveContent) {
              return
            }
            
            await new Promise(resolve => setTimeout(resolve, 200))
          }
          
        }

        // First, check ALL pages in DOM (including hidden ones) to diagnose
        const allPagesInDOM = Array.from(document.querySelectorAll('.prescription-page'))
        const allPagesDiagnostic = allPagesInDOM.map((p, i) => {
          const style = window.getComputedStyle(p)
          const rect = p.getBoundingClientRect()
          return {
            index: i,
            pageNumber: p.getAttribute('data-page-number'),
            display: style.display,
            visibility: style.visibility,
            opacity: style.opacity,
            width: rect.width,
            height: rect.height,
            inPrintRxDrawer: !!p.closest('[data-print-rx-drawer-container="true"]'),
            inRxDrawer: !!p.closest('.PMPPrintWrapper'),
            inOtherDrawer: !!p.closest('.MuiDrawer-root') && !p.closest('[data-print-rx-drawer-container="true"]'),
            parentTag: p.parentElement?.tagName,
            parentClass: p.parentElement?.className?.substring(0, 50),
            parentDisplay: window.getComputedStyle(p.parentElement)?.display,
            parentVisibility: window.getComputedStyle(p.parentElement)?.visibility
          }
        })
        
        
        // Re-query pages after layout settlement - use EXACT same logic as quick print button
        // The quick print button queries pages directly without waiting for multipass
        let finalPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
          // Check if page itself is visible
          const pageStyle = window.getComputedStyle(page)
          if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
            return false
          }
          
          // Exclude pages in PrintRx drawer (our own drawer) - these are clones we created
          if (page.closest('[data-print-rx-drawer-container="true"]')) {
            return false
          }
          
          // Check if page is in a visible container (not hidden drawer/modal) - same as quick print
          let parent = page.parentElement
          while (parent && parent !== document.body) {
            const style = window.getComputedStyle(parent)
            // Skip if parent is hidden
            if (style.display === 'none' || style.visibility === 'hidden') {
              return false
            }
            // Skip if parent is a closed drawer (MUI Drawer with display: none)
            if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
              return false
            }
            parent = parent.parentElement
          }
          return true
        })
        
        
        // Wait for pages to have content before proceeding
        if (finalPages.length > 0) {
          await waitForPageContent(finalPages)
        }
        
        // If we still don't have enough pages, wait longer and re-query multiple times
        // The multipass algorithm might still be rendering pages, or pages might be in Rx drawer that's not open
        let retryCount = 0
        const maxRetries = 5
        while (finalPages.length < 2 && retryCount < maxRetries) {
          
          // Wait longer for pages to render (multipass algorithm might need more time)
          await new Promise(resolve => setTimeout(resolve, 1000))
          
          // Re-check all pages in DOM
          const allPagesCheck = Array.from(document.querySelectorAll('.prescription-page'))
          const allPagesCheckDiagnostic = allPagesCheck.map((p, i) => {
            const style = window.getComputedStyle(p)
            return {
              index: i,
              pageNumber: p.getAttribute('data-page-number'),
              display: style.display,
              visibility: style.visibility,
              opacity: style.opacity,
              inPrintRxDrawer: !!p.closest('[data-print-rx-drawer-container="true"]'),
              inRxDrawer: !!p.closest('.PMPPrintWrapper'),
              inOtherDrawer: !!p.closest('.MuiDrawer-root') && !p.closest('[data-print-rx-drawer-container="true"]')
            }
          })
          
          
          // Re-query pages with same filter logic
          finalPages = Array.from(document.querySelectorAll('.prescription-page')).filter(page => {
            const pageStyle = window.getComputedStyle(page)
            if (pageStyle.display === 'none' || pageStyle.visibility === 'hidden' || pageStyle.opacity === '0') {
              return false
            }
            if (page.closest('[data-print-rx-drawer-container="true"]')) {
              return false
            }
            let parent = page.parentElement
            while (parent && parent !== document.body) {
              const style = window.getComputedStyle(parent)
              if (style.display === 'none' || style.visibility === 'hidden') {
                return false
              }
              if (parent.classList.contains('MuiDrawer-root') && style.display === 'none') {
                return false
              }
              parent = parent.parentElement
            }
            return true
          })
          
          
          retryCount++
        }
        

        if (finalPages.length > allPages.length) {
          allPages = finalPages
        }

        // Deduplicate by page number - same logic as quick print button
        // Prefer main page over drawer page when there are duplicates
        const pagesByNumber = new Map()
        allPages.forEach(page => {
          const pageNum = page.getAttribute('data-page-number') || page.getAttribute('data-pdf-page')
          if (pageNum) {
            if (!pagesByNumber.has(pageNum)) {
              pagesByNumber.set(pageNum, page)
            } else {
              const existingPage = pagesByNumber.get(pageNum)
              // Check if pages are in drawer (PMPPrintWrapper is the Rx drawer container)
              const isExistingInDrawer = existingPage.closest('.PMPPrintWrapper') !== null
              const isCurrentInDrawer = page.closest('.PMPPrintWrapper') !== null
              // Prefer main page over drawer page (same as quick print button)
              if (isExistingInDrawer && !isCurrentInDrawer) {
                pagesByNumber.set(pageNum, page)
              }
            }
          } else {
            pagesByNumber.set(`no-number-${pagesByNumber.size}`, page)
          }
        })
        

        allPages = Array.from(pagesByNumber.values())
        allPages.sort((a, b) => {
          const pageNumA = parseInt(a.getAttribute('data-page-number') || a.getAttribute('data-pdf-page')) || 0
          const pageNumB = parseInt(b.getAttribute('data-page-number') || b.getAttribute('data-pdf-page')) || 0
          return pageNumA - pageNumB
        })

        if (!allPages || allPages.length === 0) {
          toast.error('No prescription pages found to print', { autoClose: 3000 })
          toast.dismiss(loadingToastId)
          setIsLoading(false)
          return
        }

        toast.dismiss(loadingToastId)

        // Helper functions for cloning
        const imageToBase64 = async (url, originalImgElement = null) => {
          if (originalImgElement && originalImgElement.complete && originalImgElement.naturalHeight !== 0) {
            try {
              const imgUrl = new URL(originalImgElement.src, window.location.href)
              const isSameOrigin = imgUrl.origin === window.location.origin

              if (isSameOrigin || originalImgElement.crossOrigin !== null) {
                const canvas = document.createElement('canvas')
                canvas.width = originalImgElement.naturalWidth || originalImgElement.width
                canvas.height = originalImgElement.naturalHeight || originalImgElement.height
                const ctx = canvas.getContext('2d')
                ctx.drawImage(originalImgElement, 0, 0)
                return canvas.toDataURL('image/png')
              }
            } catch (e) {
              console.warn('Could not get image from DOM element:', url)
            }
          }

          try {
            const response = await fetch(url, {
              mode: 'cors',
              credentials: 'omit',
              headers: { 'Accept': 'image/*' }
            })
            if (response.ok) {
              const blob = await response.blob()
              return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.onloadend = () => resolve(reader.result)
                reader.onerror = () => reject(new Error('FileReader failed'))
                reader.readAsDataURL(blob)
              })
            }
          } catch (fetchError) {
            console.warn('Fetch failed for image:', url)
          }

          return null
        }

        const copyComputedStyles = (original, cloned) => {
          const computed = window.getComputedStyle(original)
          const styleProps = [
            'display', 'visibility', 'opacity', 'position',
            'margin', 'marginTop', 'marginRight', 'marginBottom', 'marginLeft',
            'padding', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
            'border', 'borderTop', 'borderRight', 'borderBottom', 'borderLeft',
            'borderWidth', 'borderStyle', 'borderColor', 'borderRadius',
            'textAlign', 'fontSize', 'fontWeight', 'fontFamily', 'lineHeight', 'color',
            'backgroundColor', 'background', 'backgroundImage', 'backgroundSize', 'backgroundPosition', 'backgroundRepeat',
            'boxSizing', 'flexDirection', 'flexWrap', 'justifyContent', 'alignItems', 'alignContent', 'flex', 'flexGrow', 'flexShrink', 'flexBasis',
            'textDecoration', 'textTransform', 'letterSpacing', 'wordSpacing',
            'verticalAlign', 'whiteSpace', 'overflow', 'overflowX', 'overflowY',
            'zIndex', 'transform', 'transformOrigin',
            'borderCollapse', 'borderSpacing', 'tableLayout', 'captionSide',
            'boxShadow', 'textShadow', 'outline', 'outlineWidth', 'outlineStyle', 'outlineColor',
          ]

          styleProps.forEach(prop => {
            const value = computed.getPropertyValue(prop)
            if (value && value !== 'none' && value !== 'normal' && value !== 'auto' && value !== '0px') {
              cloned.style.setProperty(prop, value)
            }
          })

          const borderWidth = computed.borderWidth
          const borderStyle = computed.borderStyle
          const borderColor = computed.borderColor
          if (borderWidth && borderWidth !== '0px' && borderStyle && borderStyle !== 'none') {
            cloned.style.border = `${borderWidth} ${borderStyle} ${borderColor}`
          }
        }

        // Clone pages (same as quick print)
        const clonedPages = []
        for (let idx = 0; idx < allPages.length; idx++) {
          const pageElement = allPages[idx]
          if (!pageElement) continue

          const cloned = pageElement.cloneNode(true)

          // Remove hide-on-print elements
          const hideElements = cloned.querySelectorAll('.hide-on-print-only, .hide-on-print')
          hideElements.forEach(el => el.remove())

          // Convert images to base64
          const images = cloned.querySelectorAll('img')
          for (const clonedImg of images) {
            const src = clonedImg.getAttribute('src') || clonedImg.src
            if (src && !src.startsWith('data:')) {
              try {
                let originalImg = null
                try {
                  const originalImages = pageElement.querySelectorAll('img')
                  originalImg = Array.from(originalImages).find(img => {
                    try {
                      return img.src === src ||
                        img.getAttribute('src') === src ||
                        new URL(img.src, window.location.href).href === new URL(src, window.location.href).href
                    } catch (e) {
                      return img.src === src || img.getAttribute('src') === src
                    }
                  })
                } catch (e) {
                  console.warn('Error finding original image element:', e)
                }

                const base64 = await imageToBase64(src, originalImg)
                if (base64 && base64.startsWith('data:')) {
                  clonedImg.src = base64
                }
              } catch (error) {
                console.error('Failed to convert image to base64:', src, error)
              }
            }
          }

          // Copy computed styles recursively
          const copyStylesRecursive = (orig, clone) => {
            copyComputedStyles(orig, clone)
            const origChildren = orig.children
            const cloneChildren = clone.children
            for (let i = 0; i < origChildren.length && i < cloneChildren.length; i++) {
              copyStylesRecursive(origChildren[i], cloneChildren[i])
            }
          }
          copyStylesRecursive(pageElement, cloned)

          // Style the cloned page for print (same as quick print)
          // NOTE: We set width here for drawer display, but print CSS will override it
          const originalComputed = window.getComputedStyle(pageElement)
          cloned.style.width = `${widthOfA4Page}px`
          cloned.style.maxWidth = `${widthOfA4Page}px`
          cloned.style.minWidth = `${widthOfA4Page}px`
          cloned.style.height = `${heightOfA4Page}px`
          cloned.style.minHeight = `${heightOfA4Page}px`
          cloned.style.maxHeight = `${heightOfA4Page}px`
          cloned.style.margin = '0 auto'
          cloned.style.padding = '0'
          cloned.style.display = 'flex'
          cloned.style.flexDirection = 'column'
          cloned.style.position = 'relative'
          cloned.style.boxSizing = 'border-box'
          cloned.style.overflow = 'hidden'
          cloned.style.pageBreakAfter = idx < allPages.length - 1 ? 'always' : 'auto'
          cloned.style.pageBreakInside = 'avoid'
          
          // Add data attribute to help print CSS target it
          cloned.setAttribute('data-print-page', 'true')

          // Apply flex to direct children
          const directChildren = cloned.children
          for (let i = 0; i < directChildren.length; i++) {
            const child = directChildren[i]
            child.style.display = 'flex'
            child.style.flexDirection = 'column'
            child.style.flex = '1'
            child.style.minHeight = '0'
          }

          if (originalComputed.borderWidth && originalComputed.borderWidth !== '0px' &&
            originalComputed.borderStyle && originalComputed.borderStyle !== 'none') {
            cloned.style.border = originalComputed.border
          }

          cloned.classList.add('prescription-page')
          clonedPages.push(cloned)
        }

        // Convert cloned pages to HTML string
        const pagesHTML = clonedPages.map(page => page.outerHTML).join('')
        
        
        
        try {
          
          setClonedPagesHTML(pagesHTML)
          
          
          setIsLoading(false)
          
        } catch (stateError) {
          throw stateError
        }
        
      } catch (error) {
        console.error('Error in quick print flow:', error)
        toast.error('Failed to prepare pages for printing', { autoClose: 3000 })
        setIsLoading(false)
      }
    }

    // Start the flow when drawer opens
    // The quick print button works because it's called from within the Rx drawer when it's already open
    // We need to wait for pages to be rendered - they might be on the main page or in the Rx drawer
    // After hard refresh, the multipass algorithm needs more time to complete
    
    
    const timer = setTimeout(async () => {
      
      try {
        // executeQuickPrintFlow already handles waiting for Rx drawer and multipass algorithm internally
        // Just call it directly - no need to wait here since it's async and handles its own timing
        
        executeQuickPrintFlow()
        
      } catch (error) {
        console.error('Error in executeQuickPrintFlow setup:', error)
        setIsLoading(false)
      }
    }, 500) // Initial delay for drawer to open

    return () => clearTimeout(timer)
  }, [prescriptionRef])

  // Auto-trigger print when content is ready
  useEffect(() => {
    if (!clonedPagesHTML || isLoading || printTriggeredRef.current) {
      return
    }

    const triggerPrint = () => {
      if (printTriggeredRef.current) {
        return
      }

      const container = containerRef.current
      if (!container) {
        setTimeout(triggerPrint, 200)
        return
      }

      const pages = container.querySelectorAll('.prescription-page')
      
      
      if (pages.length === 0) {
        setTimeout(triggerPrint, 200)
        return
      }

      printTriggeredRef.current = true
      
      // Force container width again right before print (in case it was reset)
      const rootContainer = container?.closest('[data-print-rx-drawer-container="true"]')
      const drawerPaper = container?.closest('.MuiDrawer-paper')
      if (rootContainer && drawerPaper) {
        const paperRect = drawerPaper.getBoundingClientRect()
        const actualPaperWidth = paperRect.width
        
        // Check for any borders that might reduce the available width
        const paperComputed = window.getComputedStyle(drawerPaper)
        const paperBorderLeft = parseFloat(paperComputed.borderLeftWidth) || 0
        const paperBorderRight = parseFloat(paperComputed.borderRightWidth) || 0
        const availableWidth = actualPaperWidth - paperBorderLeft - paperBorderRight
        
        // Force width on root container using !important via setProperty
        rootContainer.style.setProperty('width', `${availableWidth}px`, 'important')
        rootContainer.style.setProperty('min-width', `${availableWidth}px`, 'important')
        rootContainer.style.setProperty('max-width', `${availableWidth}px`, 'important')
        rootContainer.style.setProperty('margin', '0', 'important')
        rootContainer.style.setProperty('padding', '0', 'important')
        rootContainer.style.setProperty('box-sizing', 'border-box', 'important')
        
        // Force width on the direct child Box
        const parentBox = rootContainer.firstElementChild
        if (parentBox) {
          parentBox.style.setProperty('width', `${availableWidth}px`, 'important')
          parentBox.style.setProperty('min-width', `${availableWidth}px`, 'important')
          parentBox.style.setProperty('max-width', `${availableWidth}px`, 'important')
          parentBox.style.setProperty('margin', '0', 'important')
          parentBox.style.setProperty('padding', '0', 'important')
          parentBox.style.setProperty('box-sizing', 'border-box', 'important')
        }
        
        // Force width on the print-rx-container (the actual scrollable container)
        if (container) {
          container.style.setProperty('width', `${availableWidth}px`, 'important')
          container.style.setProperty('min-width', `${availableWidth}px`, 'important')
          container.style.setProperty('max-width', `${availableWidth}px`, 'important')
          container.style.setProperty('padding-left', '0', 'important')
          container.style.setProperty('padding-right', '0', 'important')
          container.style.setProperty('margin-left', '0', 'important')
          container.style.setProperty('margin-right', '0', 'important')
          container.style.setProperty('box-sizing', 'border-box', 'important')
        }
        
        // Force a reflow to ensure width changes take effect
        void rootContainer.offsetHeight
        void container?.offsetHeight
        
      }
      
      // Small delay to ensure width changes take effect before measuring
      setTimeout(() => {
        // Re-measure after forcing width to verify it took effect
        const rootContainerAfter = container?.closest('[data-print-rx-drawer-container="true"]')
        const drawerPaperAfter = container?.closest('.MuiDrawer-paper')
        if (rootContainerAfter && drawerPaperAfter && rootContainerAfter.offsetWidth !== drawerPaperAfter.offsetWidth) {
          // Width didn't take effect, try again with more aggressive approach
          const paperWidth = drawerPaperAfter.offsetWidth
          rootContainerAfter.style.cssText += `width: ${paperWidth}px !important; min-width: ${paperWidth}px !important; max-width: ${paperWidth}px !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important;`
          const parentBoxAfter = rootContainerAfter.firstElementChild
          if (parentBoxAfter) {
            parentBoxAfter.style.cssText += `width: ${paperWidth}px !important; min-width: ${paperWidth}px !important; max-width: ${paperWidth}px !important; margin: 0 !important; padding: 0 !important; box-sizing: border-box !important;`
          }
          if (container) {
            container.style.cssText += `width: ${paperWidth}px !important; min-width: ${paperWidth}px !important; max-width: ${paperWidth}px !important; padding-left: 0 !important; padding-right: 0 !important; margin-left: 0 !important; margin-right: 0 !important; box-sizing: border-box !important;`
          }
          void rootContainerAfter.offsetHeight
        }
        
      }, 50) // Close setTimeout from line 1217

      // Get A4 dimensions
      const currentDpiData = A4DpiToDetailsMap?.[96] || A4DpiToDetailsMap[96]
      const widthOfA4Page = currentDpiData?.width || 794
      const heightOfA4Page = currentDpiData?.height || 1123

      // Apply print styles directly to the drawer (no new window)
      const printStyle = document.createElement('style')
      printStyle.id = 'print-rx-drawer-print-style'
      printStyle.textContent = `
        @media print {
          @page {
            margin: 0 !important;
            size: A4;
          }
          * {
            box-sizing: border-box !important;
          }
          html, body {
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            height: auto !important;
            overflow: visible !important;
            background: white !important;
          }
          /* CRITICAL: Hide ALL prescription pages by default, then show only those in drawer */
          /* This ensures original pages are hidden and only cloned pages in drawer are shown */
          /* Step 1: Hide ALL prescription pages by default - use very specific selectors */
          body .prescription-page,
          html .prescription-page,
          .prescription-page {
            display: none !important;
            visibility: hidden !important;
            opacity: 0 !important;
            height: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
            page-break-after: auto !important;
            page-break-inside: auto !important;
            position: absolute !important;
            left: -9999px !important;
            top: -9999px !important;
          }
          /* Step 2: Show ONLY prescription pages inside the print-rx drawer container */
          /* Use very specific selectors to override the hiding rule */
          [data-print-rx-drawer-container="true"] .prescription-page,
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page,
          [data-print-rx-drawer-container="true"] .prescription-page[data-print-page="true"],
          body [data-print-rx-drawer-container="true"] .prescription-page,
          html [data-print-rx-drawer-container="true"] .prescription-page {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            height: auto !important;
            width: 794px !important;
            position: relative !important;
            left: auto !important;
            top: auto !important;
            page-break-after: always !important;
            page-break-inside: avoid !important;
          }
          /* Step 3: Last page should not have page break after */
          [data-print-rx-drawer-container="true"] .prescription-page:last-child,
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          /* Hide drawer backdrop and modal overlay, but NOT the drawer content */
          .MuiBackdrop-root {
            display: none !important;
            visibility: hidden !important;
          }
          /* Make drawer visible in print - we need it to show the content */
          /* BUT: Only target drawers that contain our print-rx container */
          .MuiDrawer-root:has([data-print-rx-drawer-container="true"]),
          .MuiDrawer-paper:has([data-print-rx-drawer-container="true"]),
          .MuiModal-root:has([data-print-rx-drawer-container="true"]) {
            position: static !important;
            display: block !important;
            visibility: visible !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
            width: 100% !important;
            max-width: 100% !important;
            left: 0 !important;
            right: 0 !important;
            top: 0 !important;
            transform: none !important;
            background: white !important;
            box-shadow: none !important;
            border: none !important;
          }
          /* Hide all other drawers in print */
          .MuiDrawer-root:not(:has([data-print-rx-drawer-container="true"])),
          .MuiDrawer-paper:not(:has([data-print-rx-drawer-container="true"])),
          .MuiModal-root:not(:has([data-print-rx-drawer-container="true"])) {
            display: none !important;
            visibility: hidden !important;
          }
          /* Hide session drawer header/controls but show content */
          .session-drawer > div:first-child:has(button),
          .session-drawer > div:first-child:has([class*="MuiTypography"]) {
            display: none !important;
          }
          /* Show print-rx container and ensure it's visible and positioned correctly */
          [data-print-rx-drawer-container="true"] {
            display: block !important;
            visibility: visible !important;
            position: static !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            left: 0 !important;
            right: 0 !important;
            top: 0 !important;
            transform: none !important;
            background: white !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          /* Ensure all children are visible */
          [data-print-rx-drawer-container="true"] * {
            visibility: visible !important;
          }
          /* Hide header if it exists */
          [data-print-rx-drawer-container="true"] > div > div:first-child:has(button),
          [data-print-rx-drawer-container="true"] > div > div:first-child:has([class*="MuiTypography"]) {
            display: none !important;
          }
          /* Reset container positioning - center it and ensure all pages are visible */
          .print-rx-container {
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
            padding: 0 !important;
            margin: 0 auto !important;
            text-align: center !important;
            position: static !important;
            left: 0 !important;
            right: 0 !important;
            transform: none !important;
            overflow: visible !important;
            overflow-x: visible !important;
            overflow-y: visible !important;
            background: white !important;
            /* Ensure all children (pages) are visible */
            contain: none !important;
            /* Force container to show all content - critical for multiple pages */
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Force page width and center it - ensure it's centered on the page */
          /* NOTE: The rule to hide all pages is above, this rule only applies to drawer pages */
          /* Use attribute selector to override inline styles - ensure these are MORE specific than the hiding rule */
          body [data-print-rx-drawer-container="true"] .prescription-page[data-page-number],
          html [data-print-rx-drawer-container="true"] .prescription-page[data-page-number],
          body [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page[data-page-number],
          html [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page[data-page-number],
          body [data-print-rx-drawer-container="true"] .prescription-page,
          html [data-print-rx-drawer-container="true"] .prescription-page,
          body [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page,
          html [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            width: ${widthOfA4Page}px !important;
            max-width: ${widthOfA4Page}px !important;
            min-width: ${widthOfA4Page}px !important;
            height: auto !important;
            min-height: ${heightOfA4Page}px !important;
            max-height: none !important;
            margin-left: auto !important;
            margin-right: auto !important;
            margin-top: 0 !important;
            margin-bottom: 0 !important;
            padding: 0 !important;
            page-break-after: always !important;
            page-break-inside: avoid !important;
            break-after: page !important;
            break-inside: avoid !important;
            background: white !important;
            box-shadow: none !important;
            position: relative !important;
            left: auto !important;
            right: auto !important;
            transform: none !important;
            float: none !important;
            clear: both !important;
            box-sizing: border-box !important;
            overflow: visible !important;
            /* Ensure page is not clipped */
            contain: none !important;
            /* Force page to be on its own page in print */
            page-break-before: auto !important;
            /* Override any inline display styles */
            flex-direction: column !important;
          }
          /* Ensure both pages are visible - target by page number */
          [data-print-rx-drawer-container="true"] .prescription-page[data-page-number="1"],
          [data-print-rx-drawer-container="true"] .prescription-page[data-page-number="2"],
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page[data-page-number="1"],
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page[data-page-number="2"] {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          /* Ensure last page in drawer doesn't have page break after */
          [data-print-rx-drawer-container="true"] .prescription-page:last-child,
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          /* CRITICAL: Force all pages in drawer to be visible - target each page explicitly */
          [data-print-rx-drawer-container="true"] .print-rx-container > .prescription-page,
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: relative !important;
            page-break-after: always !important;
            break-after: page !important;
          }
          /* Last page should not have page break after */
          [data-print-rx-drawer-container="true"] .print-rx-container > .prescription-page:last-child,
          [data-print-rx-drawer-container="true"] .print-rx-container .prescription-page:last-child {
            page-break-after: auto !important;
            break-after: auto !important;
          }
          /* Ensure all content inside pages is visible - don't force display:block on everything */
          .prescription-page * {
            visibility: visible !important;
          }
          /* Override any hidden styles */
          .prescription-page *[style*="display: none"],
          .prescription-page *[style*="visibility: hidden"] {
            visibility: visible !important;
          }
          /* Ensure flex/block elements maintain their display */
          .prescription-page [style*="display: flex"] {
            display: flex !important;
          }
          .prescription-page [style*="display: block"] {
            display: block !important;
          }
          .prescription-page [style*="display: inline"] {
            display: inline !important;
          }
          .prescription-page [style*="display: inline-block"] {
            display: inline-block !important;
          }
          .prescription-page:last-child {
            page-break-after: auto !important;
          }
        }
      `
      document.head.appendChild(printStyle)
      

        // Add event listener to check print state
        const checkPrintState = () => {
          const container = document.querySelector('[data-print-rx-drawer-container="true"]')
          const pages = container?.querySelectorAll('.prescription-page')
          const firstPage = pages?.[0]
          
        }
        
        // Check state before print
        checkPrintState()
        
        // Trigger print directly from drawer
        setTimeout(() => {
          // Verify all pages are in the DOM and have content before printing
          const allPagesInDOM = container.querySelectorAll('.prescription-page')
          const pagesWithContent = Array.from(allPagesInDOM).filter(page => {
            const hasText = page.textContent && page.textContent.trim().length > 100
            const hasChildren = page.children.length > 0
            const htmlLength = page.outerHTML.length
            return hasText || hasChildren || htmlLength > 1000
          })
          
          // Ensure container height accommodates all pages
          if (container && allPagesInDOM.length > 0) {
            const totalHeight = Array.from(allPagesInDOM).reduce((sum, page) => {
              const rect = page.getBoundingClientRect()
              return sum + rect.height
            }, 0)
            // Force container to have enough height for all pages
            container.style.height = 'auto'
            container.style.maxHeight = 'none'
            container.style.minHeight = `${totalHeight}px`
            
            // Also ensure the root container has enough height
            const rootContainer = container.closest('[data-print-rx-drawer-container="true"]')
            if (rootContainer) {
              rootContainer.style.height = 'auto'
              rootContainer.style.maxHeight = 'none'
              rootContainer.style.minHeight = `${totalHeight}px`
            }
            
            // Ensure drawer paper has enough height
            const drawerPaper = container.closest('.MuiDrawer-paper')
            if (drawerPaper) {
              drawerPaper.style.height = 'auto'
              drawerPaper.style.maxHeight = 'none'
              drawerPaper.style.minHeight = `${totalHeight}px`
            }
            
          }
          
          
          // Before printing, explicitly hide all pages outside the drawer using JavaScript
          // This ensures they're hidden even if CSS doesn't apply correctly
          const allPagesInDocument = Array.from(document.querySelectorAll('.prescription-page'))
          const pagesToHide = allPagesInDocument.filter(page => 
            !page.closest('[data-print-rx-drawer-container="true"]')
          )
          
          // Store original styles so we can restore them after print
          const originalStyles = pagesToHide.map(page => ({
            element: page,
            display: page.style.display,
            visibility: page.style.visibility,
            opacity: page.style.opacity,
            position: page.style.position,
            left: page.style.left
          }))
          
          // Hide original pages
          pagesToHide.forEach(page => {
            page.style.setProperty('display', 'none', 'important')
            page.style.setProperty('visibility', 'hidden', 'important')
            page.style.setProperty('opacity', '0', 'important')
            page.style.setProperty('position', 'absolute', 'important')
            page.style.setProperty('left', '-9999px', 'important')
          })
          
          // Ensure drawer pages have proper page breaks
          const drawerPages = Array.from(container.querySelectorAll('.prescription-page'))
          drawerPages.forEach((page, idx) => {
            const isLastPage = idx === drawerPages.length - 1
            // Force page break styles via JavaScript to ensure they're applied
            page.style.setProperty('page-break-after', isLastPage ? 'auto' : 'always', 'important')
            page.style.setProperty('break-after', isLastPage ? 'auto' : 'page', 'important')
            page.style.setProperty('page-break-inside', 'avoid', 'important')
            page.style.setProperty('break-inside', 'avoid', 'important')
            // Ensure page is displayed as block in print
            page.style.setProperty('display', 'block', 'important')
          })
          
          
          // Check state right before window.print()
          checkPrintState()
          window.print()
          
          // Restore original pages after print (with delay to ensure print preview is closed)
          window.addEventListener('afterprint', () => {
            setTimeout(() => {
              originalStyles.forEach(({ element, display, visibility, opacity, position, left }) => {
                if (display) element.style.display = display
                if (visibility) element.style.visibility = visibility
                if (opacity) element.style.opacity = opacity
                if (position) element.style.position = position
                if (left) element.style.left = left
              })
            }, 1000)
          }, { once: true })
          
          // Check state after print dialog opens (if possible)
          setTimeout(() => {
            
          // Check what pages are visible in print mode (after print style is applied)
          // Use a setTimeout to check after print style is applied
          setTimeout(() => {
            const allPagesAfterPrintStyle = Array.from(document.querySelectorAll('.prescription-page'))
            const visiblePagesInPrint = allPagesAfterPrintStyle.filter(page => {
              const style = window.getComputedStyle(page)
              return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0'
            })
            const pagesInDrawerAfterPrint = visiblePagesInPrint.filter(page => 
              page.closest('[data-print-rx-drawer-container="true"]')
            )
            const pagesOutsideDrawerAfterPrint = visiblePagesInPrint.filter(page => 
              !page.closest('[data-print-rx-drawer-container="true"]')
            )
            
            // Check page break styles
            const drawerPagesWithBreaks = pagesInDrawerAfterPrint.map(page => {
              const style = window.getComputedStyle(page)
              return {
                pageNumber: page.getAttribute('data-page-number'),
                pageBreakAfter: style.pageBreakAfter || style.breakAfter,
                pageBreakInside: style.pageBreakInside || style.breakInside,
                display: style.display,
                visibility: style.visibility,
                opacity: style.opacity,
                height: page.offsetHeight,
                width: page.offsetWidth
              }
            })
            
          }, 200)
          }, 100)
        }, 50) // Small delay to ensure width changes take effect

      // Clean up print style after print
      // Remove print style after print preview closes
      const removePrintStyle = () => {
        const style = document.getElementById('print-rx-drawer-print-style')
        if (style) {
          style.remove()
        }
      }
      
      window.addEventListener('afterprint', () => {
        setTimeout(removePrintStyle, 1000)
      }, { once: true })
      
      // Also remove on beforeunload to prevent style from persisting
      window.addEventListener('beforeunload', removePrintStyle, { once: true })
    }

    setTimeout(triggerPrint, 1000)
  }, [clonedPagesHTML, isLoading])

  return (
    <div 
      data-print-rx-drawer-container="true" 
      style={{ 
        width: '100%', // Full width for bottom drawer
        maxWidth: '100%', // No max width constraint for bottom drawer
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        margin: 0, // No margins - let parent handle spacing
        padding: 0, // No padding
        boxSizing: 'border-box',
        overflow: 'visible', // Ensure content is not cut off
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        height: '100%', 
        width: '100%', 
        minWidth: '100%',
        maxWidth: '100%',
        margin: 0, 
        padding: 0,
        boxSizing: 'border-box',
      }}>
        {/* Content - Header is handled by SessionDrawer wrapper */}
        {(() => {
          return null
        })()}
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : clonedPagesHTML ? (
          (() => {
            return (
              <Box
                ref={containerRef}
                className="print-rx-container"
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  overflowX: 'visible', // Allow content to be fully visible, not cut off
                  py: 2, // Only vertical padding for spacing
                  px: 0, // No horizontal padding to prevent cutoff
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  width: '100%',
                  minWidth: '100%',
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  margin: 0,
                  paddingLeft: '0 !important',
                  paddingRight: '0 !important',
                  // Don't set minWidth here - let it be flexible
                  '& .prescription-page': {
                    backgroundColor: 'white',
                    marginBottom: 2,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    width: '794px !important', // A4 width - fixed, force with !important
                    maxWidth: '794px !important', // Don't scale down - always 794px
                    minWidth: '794px !important', // Always 794px minimum
                    height: 'auto',
                    minHeight: '1123px', // A4 height
                    // Ensure page is visible and not cut off
                    boxSizing: 'border-box',
                    overflow: 'visible !important',
                    overflowX: 'visible !important', // Ensure horizontal overflow is visible
                    overflowY: 'visible !important', // Ensure vertical overflow is visible
                    flexShrink: 0, // Prevent shrinking
                    transform: 'none !important', // Prevent any transforms
                    scale: '1 !important', // Prevent scaling
                    // Ensure content inside page doesn't overflow
                    '& *': {
                      maxWidth: '100%',
                      boxSizing: 'border-box',
                    },
                  },
                }}
                dangerouslySetInnerHTML={{ __html: clonedPagesHTML }}
              />
            )
          })()
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </div>
  )
}
